/**
 * CarrierEvidencePanel — THE carrier-evidence surface. One component, one
 * uploader, one submit button, rendered identically by ora, the Outlook pane
 * and anything else that grows a decision surface later.
 *
 * ── Why this exists ───────────────────────────────────────────────────────
 * The evidence surface was three components deep and rendered TWO uploaders at
 * once. With the decision card present and the AM branch active,
 * `PendingEdocsDrawerBody` drew:
 *
 *     CarrierEvidenceCard
 *       └ EarlyEvidenceAttach      → dropzone + "Stage evidence"
 *     carrier-evidence-gate-actions
 *       └ amEvidenceUploader       → TWO dropzones + "Submit declarations page"
 *
 * Two ways to hand over the same PDF, stacked, with different labels and
 * different payloads. The operator read it as "2 upload logics", correctly.
 *
 * They were not, however, duplicates to delete: `stage` appends to a holding
 * area and is removable while the run keeps waiting, and `approve` answers the
 * parked gate and RELEASES the run. Both are load-bearing. So the two collapse
 * into one uploader whose submit VERB follows the run's state — the AM picks a
 * file once, in one place, and the panel decides whether that means "hold this"
 * or "here it is, carry on".
 *
 * ── The three defects it fixes ────────────────────────────────────────────
 *  1. Two uploaders → one.
 *  2. Every busy control replaced its own label with a bare "…" — six sites
 *     did `{flag ? "…" : "<label>"}`, which renders as an empty box. Labels
 *     now stay put and a spinner joins them.
 *  3. ONE flag drove all of them (`isHitlSubmitting || evidenceBusy`), so
 *     pressing "Skip the wait" blanked the stage button and disabled the
 *     unrelated DECISION row. The flag could also stick: it was cleared only
 *     when the snapshot's branch flipped to `am_supplies`, which never happens
 *     on the placeholder-card path — leaving the whole region disabled and
 *     silent with no way out. Busy is now per control and time-bounded.
 *
 * ── What it does NOT do ───────────────────────────────────────────────────
 *  - No transport. Every submit is a caller-supplied callback pointed at the
 *    host's EXISTING resume path. There is no second answer transport.
 *  - Never optimistic about the BRANCH. A branch reads as chosen only once the
 *    next snapshot carries it (`model.branch`). Revealing the uploader and its
 *    guidance is not the same claim: it says "here is where the file goes",
 *    which is true the moment the AM asks to take over.
 *  - Holds no cross-submit latch. The host still owns the double-submit latch;
 *    these flags are local, per-control, and only describe what is in flight.
 *
 * Import paths:
 *   ora    → ~/common/workflows/components/CarrierEvidencePanel
 *   plugin → @product-app/common/workflows/components/CarrierEvidencePanel
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import { FileUploadDropzone } from "~/common/components/FileUploadDropzone";
import type { EvidenceCardModel, StagedEvidenceDocument } from "../phaseModel";
import { emitDrawerTelemetry } from "../drawerTelemetry";
import {
  CARRIER_EVIDENCE_ALREADY_RESUMED,
  CARRIER_EVIDENCE_COPY,
  CARRIER_EVIDENCE_REQUIRED_ANNOUNCEMENT,
  CARRIER_EVIDENCE_REQUIRED_BADGE,
  CARRIER_EVIDENCE_REVERT_LABEL,
  resolveTitle,
} from "../carrierEvidenceCopy";
import {
  EVIDENCE_APPROVE_BUSY,
  EVIDENCE_ATTACHED_NEXT,
  EVIDENCE_ATTACHED_UNNAMED,
  EVIDENCE_SUBMIT_NEEDS_FILE,
  EVIDENCE_REVERT_BUSY,
  EVIDENCE_STAGE_BUSY,
  EVIDENCE_TAKEOVER_BUSY,
  EVIDENCE_TAKEOVER_GUIDANCE,
  EVIDENCE_TAKEOVER_SLOW,
  EVIDENCE_TAKEOVER_SLOW_AFTER_MS,
} from "../copy";

export type EvidenceBranchAction = "takeover" | "wait_for_carrier";

const ACCEPTED_FILE_TYPES = [".pdf", ".png", ".jpg", ".jpeg", ".docx", ".doc"];
const MAX_FILE_BYTES = 25 * 1024 * 1024;

/** How many documents one run may stage. Mirrors `MAX_EVIDENCE_DOCUMENTS` in
 *  the substrate's resolver, which is the real gate — this one only saves a
 *  round trip. A client-side cap is advisory by nature. */
export const MAX_DOCUMENTS = 10;

/**
 * The largest file Microsoft Graph accepts as a message attachment, in RAW
 * bytes: its limit is 3,145,728 base64 bytes, three quarters of that decoded.
 *
 * Deliberately NOT the same as `MAX_FILE_BYTES`, and the gap is the point. A
 * 5 MB PDF is accepted here, reaches Epic, and then silently misses the reply
 * email because `add_message_attachment` refuses it and there is no
 * upload-session fallback. Saying so at staging time is the difference between
 * a known limitation and a document that vanishes.
 */
export const EMAIL_ATTACHABLE_MAX_BYTES = Math.floor((3 * 3145728) / 4);

async function encodeDocument(file: File): Promise<{
  filename: string;
  mime_type: string;
  content_b64: string;
}> {
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return {
    filename: file.name,
    mime_type: file.type || "application/octet-stream",
    content_b64: btoa(binary),
  };
}

/**
 * The staged list the AM sees: every document the server has confirmed, plus
 * every locally-staged document the server has not caught up with yet.
 *
 * The server's copy WINS for anything it names — it carries the real `seq` and
 * the real `consumed` flag, and the local entry is only ever a placeholder for
 * the round trip. Matching is by filename and by COUNT (one server entry
 * claims at most one local entry of the same name), not by `seq`: the local
 * `seq` is a prediction, and two genuinely different files that happen to
 * share a name must survive as two rows rather than silently collapsing into
 * one — a dropped document here looks exactly like the bug this bridge exists
 * to fix.
 */
function mergeStagedDocuments(
  serverStaged: StagedEvidenceDocument[],
  optimistic: StagedEvidenceDocument[],
): StagedEvidenceDocument[] {
  if (optimistic.length === 0) return serverStaged;
  if (serverStaged.length === 0) return optimistic;
  const unclaimed = new Map<string, number>();
  serverStaged.forEach((d) => {
    unclaimed.set(d.filename, (unclaimed.get(d.filename) ?? 0) + 1);
  });
  const localOnly = optimistic.filter((doc) => {
    const left = unclaimed.get(doc.filename) ?? 0;
    if (left > 0) {
      unclaimed.set(doc.filename, left - 1);
      return false;
    }
    return true;
  });
  return localOnly.length > 0 ? [...serverStaged, ...localOnly] : serverStaged;
}

/** Label plus spinner. NEVER a bare glyph in place of the label: a button
 *  whose text is replaced by "…" reads as broken, which is what it looked
 *  like. */
function BusyLabel({ label, busy }: { label: string; busy: boolean }) {
  if (!busy) return <>{label}</>;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        aria-hidden="true"
        className="h-3 w-3 flex-shrink-0 animate-spin rounded-full border border-current/30 border-t-current"
      />
      {label}
    </span>
  );
}

export interface CarrierEvidencePanelProps {
  model: EvidenceCardModel;
  /** Switches the branch, through the host's existing resume transport. */
  onSubmitBranch?: (action: EvidenceBranchAction) => void | Promise<void>;
  /**
   * The evidence transport — ONE callback, the host's existing resume path.
   * The panel decides the `decision` verb: `stage` / `unstage` while the run is
   * still working, `approve` once it is parked at the gate.
   */
  onSubmitEvidence?: (payload: Record<string, unknown>) => void | Promise<void>;
  /**
   * The run is PARKED at the evidence gate (`pending_edocs`). Together with the
   * AM branch this is what turns the one submit button from "Stage evidence"
   * into "Submit declarations page" — `stage` holds a document while the run
   * keeps working, `approve` answers the gate and releases it. Both were real
   * operations behind two separate uploaders; the verb follows the run's state
   * now instead of the AM having to pick the right box.
   */
  parked?: boolean;
  /** The server's refusal, rendered inline. Never clears the branch. */
  submitError?: string | null;
  /** Ids for the telemetry record only — never rendered. */
  instanceId?: string | null;
  workflowId?: string | null;
  /**
   * "Somebody is working in this card." Fired on EVERY touch of any kind — a
   * click, a drag arriving, a paste, a pick, a tab into it — so the host has to
   * be idempotent. It reports presence, not a transition.
   *
   * It exists because the surfaces that embed this card can take themselves
   * away on a timer while it is on screen (the add-in drawer's running-view
   * auto-close is 10 seconds, and the card renders under it on a run that has
   * not reached the evidence gate yet). Dragging two files out of a folder
   * takes longer than that, and the pick is local state — the drawer closing
   * mid-drag drops the files with no way back. The host holds its countdown
   * on this signal.
   *
   * IT IS NOT A DECISION AND CARRIES NO PAYLOAD. Nothing here is submitted, and
   * the host must not read it as an answer to anything.
   */
  onInteract?: () => void;
}

export function CarrierEvidencePanel({
  model,
  onSubmitBranch,
  onSubmitEvidence,
  parked: parkedProp = false,
  submitError,
  instanceId,
  workflowId,
  onInteract,
}: CarrierEvidencePanelProps) {
  // ── per-control busy. Three flags, not one: pressing the take-over must not
  //    blank the stage button, and neither may touch the host's DECISION row.
  const [branchBusy, setBranchBusy] = useState<EvidenceBranchAction | null>(null);
  const [submitBusy, setSubmitBusy] = useState(false);
  const [removingSeq, setRemovingSeq] = useState<number | null>(null);
  // The AM asked to take over. Local, and NOT a claim that the branch changed —
  // it only unfolds the uploader and its guidance while the server catches up.
  const [takeoverAsked, setTakeoverAsked] = useState(false);
  const [takeoverSlow, setTakeoverSlow] = useState(false);

  const [files, setFiles] = useState<File[]>([]);
  const [otherFiles, setOtherFiles] = useState<File[]>([]);
  const [error, setError] = useState("");
  const [committed, setCommitted] = useState<StagedEvidenceDocument[]>([]);
  // Documents the AM has unstaged that the server still reports — the same
  // bridge as `committed`, pointing the other way. Pruned below as soon as the
  // server agrees they are gone.
  const [unstagedSeqs, setUnstagedSeqs] = useState<number[]>([]);

  const amBranch = model.branch === "am_supplies";
  // The approve verb belongs to the AM branch only: on the carrier branch a
  // parked run is waiting for the CARRIER's documents, and answering the gate
  // is not the AM's move.
  const parked = parkedProp && amBranch;
  // The uploader is open once the branch is the AM's, or once they have ASKED
  // for it. Before that the card is a calm wait and an uploader would be the
  // very second path this component exists to remove.
  const uploaderOpen = amBranch || takeoverAsked;

  // The server list is AUTHORITATIVE for every document it names, and
  // `committed` bridges the gap for the ones it does not name yet, so a
  // document never blinks out of the list between a successful stage and the
  // next status poll.
  //
  // MERGED, not switched. The old form — `serverStaged.length > 0 ?
  // serverStaged : committed` — dropped the whole local list the moment the
  // server confirmed ANY document, so it only ever fixed the 0→1 transition:
  // stage doc1, let the poll confirm it, stage doc2, and doc2 was invisible
  // until the next poll. `unstagedSeqs` is the same bridge for the other
  // direction: a document the AM just unstaged must leave the list now, not
  // one poll later, whether the server or the local list is the one naming it.
  const serverStaged = useMemo(
    () => model.evidenceDocuments ?? [],
    [model.evidenceDocuments],
  );
  const staged = useMemo(() => {
    const merged = mergeStagedDocuments(serverStaged, committed);
    return unstagedSeqs.length > 0
      ? merged.filter((d) => !unstagedSeqs.includes(d.seq))
      : merged;
  }, [serverStaged, committed, unstagedSeqs]);
  const hasStaged = staged.length > 0;
  /**
   * Is anything attached AT ALL — independently of whether we can NAME it.
   *
   * `evidence_attached` is a declared server fact and the per-document list is
   * a convenience beside it; the two can legitimately disagree for a beat (an
   * older substrate publishes no list, and a snapshot taken between the stage
   * and the next poll has the flag before the names). Keying the whole surface
   * on the LIST meant that gap rendered as a bare, open dropzone still asking
   * for a declarations page directly under a body reading "The evidence is
   * attached" — two contradictory instructions, which is what the operator hit
   * on abea26a6 after staging two files successfully.
   */
  const anythingAttached = hasStaged || model.evidenceAttached === true;
  const [expanded, setExpanded] = useState(false);

  const remaining = MAX_DOCUMENTS - staged.length;
  const atCap = remaining <= 0;

  // Named at staging time, not at fan-out time: "it went to Epic but not the
  // email" is only useful BEFORE the AM walks away.
  const tooBigForEmail = files.filter((f) => f.size > EMAIL_ATTACHABLE_MAX_BYTES);

  // ── telemetry (unchanged tokens; the emitter de-duplicates per
  //    (token, instance, detail) so a re-render does not multiply a refusal).
  useEffect(() => {
    if (submitError) {
      emitDrawerTelemetry("emuw_evidence_submit_rejected", {
        instanceId,
        workflowId,
        detail: model.branch,
      });
    }
  }, [submitError, instanceId, workflowId, model.branch]);

  useEffect(() => {
    if (model.alreadyResumed) {
      emitDrawerTelemetry("emuw_evidence_already_resumed", {
        instanceId,
        workflowId,
        detail: model.decisionId,
      });
    }
  }, [model.alreadyResumed, model.decisionId, instanceId, workflowId]);

  // ── the branch switch landed: drop the local busy and the slow notice. The
  //    OLD code cleared its flag here and nowhere else, so on the
  //    placeholder-card path — where the branch never flips — it stuck true and
  //    left the region disabled and wordless.
  useEffect(() => {
    if (amBranch) {
      setBranchBusy(null);
      setTakeoverSlow(false);
    }
  }, [amBranch]);

  // ── retire the unstage bridge once the server agrees. Holding a seq after
  //    the server has stopped reporting it would hide a document re-staged
  //    under the same seq — the bridge must never outlive the round trip it
  //    covers.
  useEffect(() => {
    setUnstagedSeqs((prior) => {
      if (prior.length === 0) return prior;
      const next = prior.filter((seq) => serverStaged.some((d) => d.seq === seq));
      return next.length === prior.length ? prior : next;
    });
  }, [serverStaged]);

  // A refusal releases every control: the AM has to be able to try again.
  useEffect(() => {
    if (submitError) {
      setBranchBusy(null);
      setSubmitBusy(false);
      setRemovingSeq(null);
    }
  }, [submitError]);

  // ── time-bound the branch busy. Accepted-but-not-yet-visible is a real state
  //    (the snapshot lags, or there is no recorded decision to flip), and the
  //    AM must not be trapped in it.
  const slowTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (branchBusy !== "takeover") return undefined;
    slowTimer.current = setTimeout(() => {
      setBranchBusy(null);
      setTakeoverSlow(true);
    }, EVIDENCE_TAKEOVER_SLOW_AFTER_MS);
    return () => {
      if (slowTimer.current) clearTimeout(slowTimer.current);
      slowTimer.current = null;
    };
  }, [branchBusy]);

  const fireBranch = useCallback(
    (action: EvidenceBranchAction) => {
      if (!onSubmitBranch || branchBusy) return;
      setBranchBusy(action);
      setTakeoverSlow(false);
      if (action === "takeover") setTakeoverAsked(true);
      else setTakeoverAsked(false);
      // The caller owns the failure — it comes back as `submitError`. Swallowing
      // the rejection here keeps a refused submit from becoming an unhandled
      // promise rejection in the pane; it never suppresses the message.
      void Promise.resolve(onSubmitBranch(action))
        .catch(() => undefined)
        .finally(() => {
          // Only the revert self-clears. A take-over stays busy until the
          // snapshot confirms it or the timer above gives up, because that is
          // the wait the AM is actually in.
          if (action === "wait_for_carrier") setBranchBusy(null);
        });
    },
    [onSubmitBranch, branchBusy],
  );

  /**
   * ONE boundary, not one call site per control.
   *
   * The alternative — an `onInteract()` on the take-over button, on each
   * dropzone, on the expand toggle, on the Upload label — is a list that drifts
   * the moment somebody adds a control, and the failure mode of a missed entry
   * is the pane disappearing under the AM's hands again. The listeners are on
   * the CARD, in the CAPTURE phase, so they see everything inside it —
   * including the events `FileUploadDropzone` deliberately stops from bubbling
   * (`handleDragEnter` / `handleDrop` both call `stopPropagation`).
   *
   * Coarse on purpose: reading the card holds the countdown too. Holding costs
   * nothing — the countdown only ever closed a view, it never answered
   * anything — and the failure it prevents cost the AM their attachment.
   */
  const noteInteraction = useCallback(() => {
    onInteract?.();
  }, [onInteract]);

  const addFiles =
    (bucket: File[], setBucket: (next: File[]) => void) =>
    (incoming: FileList | File[]) => {
      setError("");
      const accepted: File[] = [];
      const oversized: string[] = [];
      Array.from(incoming).forEach((f) => {
        if (f.size > MAX_FILE_BYTES) {
          oversized.push(f.name);
          return;
        }
        accepted.push(f);
      });
      const combined = [...bucket, ...accepted];
      // Against `remaining`, NOT `Math.max(remaining, 1)`. The clamp belongs on
      // the dropzone's own `maxFiles` prop (which refuses 0); using it here let
      // a file through at the cap and then silently blocked the submit.
      if (combined.length > remaining) {
        setError(
          `This run can hold ${MAX_DOCUMENTS} documents and already has ` +
            `${staged.length}. Remove one, or send the rest separately.`,
        );
        return;
      }
      if (oversized.length > 0) {
        // Skip the oversized ones and keep the rest rather than refusing the
        // batch: one bad file must not cost the others, the same rule the
        // server's fan-out follows.
        const list = oversized.map((n) => `"${n}"`).join(", ");
        setError(
          `${list} exceeded the 25 MB per-file limit and ${
            oversized.length === 1 ? "was" : "were"
          } skipped.`,
        );
      }
      setBucket(combined);
    };

  /** One submit. The VERB is the run's state, not a second button. */
  const submit = () => {
    if (!onSubmitEvidence || files.length === 0 || submitBusy || atCap) return;
    setSubmitBusy(true);
    void (async () => {
      try {
        const chosen = [...files];
        const declarations = await Promise.all(chosen.map(encodeDocument));
        // `other_documents` rides the APPROVE payload only — the key the
        // parked gate's reader already understands. The stage path has never
        // carried it, and inventing a spelling the resolver does not read is
        // how the bucket was silently discarded the first time.
        const other = parked
          ? await Promise.all(otherFiles.map(encodeDocument))
          : [];
        await Promise.resolve(
          onSubmitEvidence({
            decision: parked ? "approve" : "stage",
            field_values: {
              edoc_declarations_document: declarations,
              ...(other.length > 0 ? { other_documents: other } : {}),
            },
          }),
        );
        if (!parked) {
          setCommitted((prior) => {
            // The next free seq comes off the MERGED view. Counting `prior`
            // and the server list separately double-counted every document the
            // server had already confirmed (it appears in both), leaving a gap
            // the server's own numbering never fills — and a local entry whose
            // seq matches nothing is a row that never reconciles.
            const base = mergeStagedDocuments(serverStaged, prior).length;
            return [
              ...prior,
              ...chosen.map((f, i) => ({
                seq: base + i + 1,
                filename: f.name,
                consumed: false,
              })),
            ];
          });
          // Collapse on success. Additive staging does not change what the AM
          // needs to see — "it is staged, here is the list" — and leaving the
          // dropzone open reads as though the submit did not take.
          setExpanded(false);
        }
        setFiles([]);
        setOtherFiles([]);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Could not read the file.");
      } finally {
        setSubmitBusy(false);
      }
    })();
  };

  const remove = (doc: StagedEvidenceDocument) => {
    if (!onSubmitEvidence || removingSeq !== null) return;
    setError("");
    setRemovingSeq(doc.seq);
    void (async () => {
      try {
        await Promise.resolve(
          onSubmitEvidence({ decision: "unstage", field_values: { seq: doc.seq } }),
        );
        setCommitted((prior) => prior.filter((d) => d.seq !== doc.seq));
        // The same bridge the stage path uses, pointing the other way: pruning
        // only `committed` left a SERVER-named document on screen until the
        // next poll, so the AM's remove read as though it had not taken.
        setUnstagedSeqs((prior) =>
          prior.includes(doc.seq) ? prior : [...prior, doc.seq],
        );
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Could not remove that document.",
        );
      } finally {
        setRemovingSeq(null);
      }
    })();
  };

  const copy = CARRIER_EVIDENCE_COPY[model.presentation];
  const title = resolveTitle(model.presentation, model.carrierName);
  const submitLabel = parked ? "Submit declarations page" : "Stage evidence";
  const submitBusyCopy = parked ? EVIDENCE_APPROVE_BUSY : EVIDENCE_STAGE_BUSY;
  const canSubmit = files.length > 0 && !submitBusy && !atCap;

  const dropzones = (
    <>
      <p className="text-2xs text-primary-gray-light-600">
        If you&rsquo;re on the carrier hand-off step (e.g. &ldquo;I&rsquo;ve
        contacted the carrier&rdquo;), staging a file here immediately submits
        that confirmation too — only stage here once you&rsquo;ve actually
        contacted the carrier. ora keeps these files if you leave this screen.
      </p>
      <div
        role="group"
        aria-label="Declarations page"
        data-testid="carrier-evidence-declarations-dropzone"
      >
        <FileUploadDropzone
          compact
          acceptedFileTypes={ACCEPTED_FILE_TYPES}
          fileUploadMaxSize={MAX_FILE_BYTES}
          maxFiles={Math.max(remaining, 1)}
          // The dropzone reports only the NEW batch on each drop/pick/paste and
          // owns no list of its own, so the consumer accumulates — the same
          // contract HitlFileUploadDecisionPanel works to.
          uploadedFiles={files as unknown as FileList}
          onFileChange={addFiles(files, setFiles)}
          onFileRemove={(index: number) => {
            setError("");
            setFiles((prior) => prior.filter((_, i) => i !== index));
          }}
        />
      </div>
      {/* The second bucket exists only where its payload key does. */}
      {parked && (
        <div
          role="group"
          aria-label="Other documents"
          data-testid="carrier-evidence-other-dropzone"
        >
          <p className="text-2xs text-primary-gray-light-600">
            Other documents (optional)
          </p>
          <FileUploadDropzone
            compact
            acceptedFileTypes={ACCEPTED_FILE_TYPES}
            fileUploadMaxSize={MAX_FILE_BYTES}
            uploadedFiles={otherFiles as unknown as FileList}
            onFileChange={addFiles(otherFiles, setOtherFiles)}
            // Functional updater, exactly like the declarations dropzone
            // above: reading the closed-over `otherFiles` silently dropped one
            // of two removals scheduled before the same commit.
            onFileRemove={(index: number) => {
              setOtherFiles((prior) => prior.filter((_, i) => i !== index));
            }}
          />
        </div>
      )}
      {tooBigForEmail.length > 0 ? (
        <span
          role="status"
          className="text-2xs text-primary-warning-700"
          data-testid="carrier-evidence-email-size-warning"
        >
          {tooBigForEmail.map((f) => f.name).join(", ")}
          {tooBigForEmail.length === 1 ? " is" : " are"} too large to attach to
          the reply email. {tooBigForEmail.length === 1 ? "It" : "They"} will
          still reach Epic — send {tooBigForEmail.length === 1 ? "it" : "them"}{" "}
          to the client another way.
        </span>
      ) : null}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          disabled={!canSubmit}
          onClick={submit}
          title={files.length === 0 ? EVIDENCE_SUBMIT_NEEDS_FILE : undefined}
          className="self-start h-8 px-3 inline-flex items-center btn-primary rounded-lg text-2xs font-semibold disabled:opacity-60"
          data-testid="carrier-evidence-submit"
        >
          <BusyLabel
            label={submitBusy ? submitBusyCopy : submitLabel}
            busy={submitBusy}
          />
        </button>
        {/* A disabled control that does not say why is the defect this panel
            was rewritten to remove. It must not come back on the submit just
            because it was fixed on the branch button. */}
        {files.length === 0 && !submitBusy ? (
          <span
            className="text-2xs text-primary-gray-light-500"
            data-testid="carrier-evidence-submit-hint"
          >
            {EVIDENCE_SUBMIT_NEEDS_FILE}
          </span>
        ) : null}
      </div>
    </>
  );

  return (
    <div
      className="rounded-lg border border-primary-gray-light-200 bg-base-white p-3 mb-2 flex flex-col gap-1"
      data-testid="carrier-evidence-card"
      data-presentation={model.presentation}
      data-branch={model.branch}
      // See `noteInteraction`. Capture phase, so a child that stops the event
      // from bubbling cannot make the host miss it.
      onClickCapture={noteInteraction}
      onDragEnterCapture={noteInteraction}
      onPasteCapture={noteInteraction}
      onFocusCapture={noteInteraction}
      // The file input is inside the dropzone: in a browser its label is
      // clicked first and `onClickCapture` has already fired, but a pick that
      // arrives without one must count too — the native file dialog it opens
      // is exactly the wait that outlives a ten-second countdown.
      onChangeCapture={noteInteraction}
    >
      <div className="flex items-center gap-2">
        <p className="flex-1 text-2xs font-semibold uppercase tracking-wide text-primary-gray-light-500">
          Carrier evidence
        </p>
        {model.evidenceRequired && (
          <span
            className="shrink-0 rounded px-1.5 py-0.5 border border-primary-warning-300 bg-primary-warning-50 text-2xs font-semibold uppercase tracking-wide text-primary-warning-800"
            data-testid="carrier-evidence-required-badge"
          >
            {CARRIER_EVIDENCE_REQUIRED_BADGE}
          </span>
        )}
      </div>

      <p className="text-sm font-semibold text-primary-gray-light-800">{title}</p>
      <p className="text-xs text-primary-gray-light-700">{copy.body}</p>

      {/* The transition to "evidence required" is ANNOUNCED, and the badge above
          carries text — the state is never conveyed by colour alone. */}
      <p className="sr-only" role="status" aria-live="polite">
        {model.evidenceRequired ? CARRIER_EVIDENCE_REQUIRED_ANNOUNCEMENT : ""}
      </p>

      {model.alreadyResumed ? (
        // WITHDRAWN, not merely disabled: there is no branch left to choose.
        <p
          className="text-xs text-primary-gray-light-700"
          data-testid="carrier-evidence-already-resumed"
        >
          {CARRIER_EVIDENCE_ALREADY_RESUMED}
        </p>
      ) : (
        <div className="mt-1 flex items-center gap-2 flex-wrap">
          {!amBranch && onSubmitBranch && (
            <button
              type="button"
              disabled={branchBusy !== null}
              onClick={() => fireBranch("takeover")}
              className="h-8 px-3 inline-flex items-center btn-primary rounded-lg text-2xs font-semibold disabled:opacity-60"
              data-testid="carrier-evidence-takeover"
            >
              <BusyLabel
                label={
                  branchBusy === "takeover"
                    ? EVIDENCE_TAKEOVER_BUSY
                    : copy.primaryAction
                }
                busy={branchBusy === "takeover"}
              />
            </button>
          )}
          {/* The AM branch offers NO action here. It used to echo
              `copy.primaryAction` as green semibold text, which reads as a link
              and is not one — and on the `attached` presentation it invited
              "Attach another" an inch above the control that actually attaches.
              Removed 2026-09-08 (operator). The branch stays legible without
              it: the title, the body and the REQUIRED badge all move with it. */}
          {model.offerRevert && onSubmitBranch && (
            <button
              type="button"
              disabled={branchBusy !== null}
              onClick={() => fireBranch("wait_for_carrier")}
              className="text-2xs text-primary-gray-light-700 hover:underline disabled:opacity-60"
              data-testid="carrier-evidence-revert"
            >
              <BusyLabel
                label={
                  branchBusy === "wait_for_carrier"
                    ? EVIDENCE_REVERT_BUSY
                    : CARRIER_EVIDENCE_REVERT_LABEL
                }
                busy={branchBusy === "wait_for_carrier"}
              />
            </button>
          )}
          {submitError && (
            <span
              role="alert"
              className="text-2xs text-primary-error-700"
              data-testid="carrier-evidence-error"
            >
              {submitError}
            </span>
          )}
        </div>
      )}

      {/* ── the ONE uploader ──────────────────────────────────────────────── */}
      {uploaderOpen && !model.alreadyResumed ? (
        <div
          className="mt-2 flex flex-col gap-1"
          data-testid="carrier-evidence-attach"
        >
          {/* The guidance the old card never gave: it disabled its own button
              and said nothing about the next step. */}
          {anythingAttached ? (
            <p
              className="text-2xs text-primary-success-700"
              data-testid="carrier-evidence-next"
            >
              {EVIDENCE_ATTACHED_NEXT}
            </p>
          ) : (
            <p
              className="text-2xs text-primary-gray-light-700"
              data-testid="carrier-evidence-guidance"
            >
              {EVIDENCE_TAKEOVER_GUIDANCE}
            </p>
          )}
          {takeoverSlow && (
            <p
              role="status"
              className="text-2xs text-primary-warning-700"
              data-testid="carrier-evidence-takeover-slow"
            >
              {EVIDENCE_TAKEOVER_SLOW}
            </p>
          )}

          {anythingAttached && !hasStaged ? (
            <div className="flex items-center gap-2">
              <p
                className="flex-1 text-2xs font-semibold text-primary-success-700"
                data-testid="carrier-evidence-attached-unnamed"
              >
                {EVIDENCE_ATTACHED_UNNAMED}
              </p>
              {/* The named case reopens the dropzone through the staged-list
                  toggle; this case has no list, so it needs its own way back. */}
              <button
                type="button"
                onClick={() => setExpanded((open) => !open)}
                className="text-2xs font-semibold text-primary-gray-light-600 hover:underline"
                data-testid="carrier-evidence-add-another"
              >
                {expanded ? "Hide" : "Add another"}
              </button>
            </div>
          ) : null}

          {hasStaged ? (
            <>
              <button
                type="button"
                aria-expanded={expanded}
                onClick={() => setExpanded((open) => !open)}
                className="flex items-center gap-1 text-left"
                data-testid="carrier-evidence-toggle"
              >
                <ChevronRightIcon
                  fontSize="small"
                  className={`text-primary-gray-light-500 transition-transform ${
                    expanded ? "rotate-90" : ""
                  }`}
                />
                <span
                  className="flex-1 text-2xs font-semibold text-primary-success-700"
                  data-testid="carrier-evidence-staged-name"
                >
                  Staged: {staged.map((d) => d.filename).join(", ")}
                </span>
                <span className="text-2xs font-semibold text-primary-gray-light-600">
                  {expanded ? "Hide" : "Add another"}
                </span>
              </button>
              <ul
                className="flex flex-col gap-0.5"
                data-testid="carrier-evidence-staged-list"
              >
                {staged.map((doc) => (
                  <li
                    // Name as well as seq: the merged list can briefly hold a
                    // local entry whose PREDICTED seq matches a server row it
                    // is not, and two rows under one React key is how a
                    // superseded row's state lands on its neighbour.
                    key={`${doc.seq}-${doc.filename}`}
                    className="flex items-center gap-1 text-2xs text-primary-gray-light-700"
                  >
                    <span className="flex-1 truncate">{doc.filename}</span>
                    {doc.consumed ? (
                      // A consumed document has no remove control. It is
                      // already on the endorsement and in the reply; offering
                      // to take it back would be a lie.
                      <span
                        className="text-2xs text-primary-gray-light-500"
                        data-testid={`carrier-evidence-sent-${doc.seq}`}
                      >
                        Sent
                      </span>
                    ) : (
                      <button
                        type="button"
                        disabled={removingSeq !== null}
                        onClick={() => remove(doc)}
                        aria-label={`Remove ${doc.filename}`}
                        className="p-0.5 rounded hover:bg-primary-gray-light-100 disabled:opacity-60"
                        data-testid={`carrier-evidence-remove-${doc.seq}`}
                      >
                        <CloseIcon sx={{ fontSize: 12 }} />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </>
          ) : null}

          {anythingAttached && !expanded ? null : dropzones}

          {error ? (
            <span role="alert" className="text-2xs text-primary-error-700">
              {error}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default CarrierEvidencePanel;
